
export default {
  meta: {
    blocks: {
      unwind: {
        public: true,
        dealer: false,
      },
    },
  },

  data() {
    return {
      pageType: null,
      pageData: null,
    };
  },

  async asyncData({ $axios, route, error, store }) {
    const SLUG = route.params.slug;
    // On the client a previously rendered product link may already tell us the type, letting us
    // skip the resolve call entirely. On the server there is no such hint, so ask the backend --
    // one indexed slug lookup, versus probing /catalog-type and waiting out a 404.
    // The hint has to name THIS slug: it is set in a router guard, so a navigation that never
    // reaches asyncData would otherwise leave it set for whatever page is opened next.
    const HINTED_PRODUCT =
      process.client && store.state.SLUG_HINT.slug === SLUG;

    if (process.client) store.commit("SLUG_HINT/CLEAR");

    const loadProduct = async () => {
      const product = await $axios.$get("product", { params: { id: SLUG } });
      // The category behind the breadcrumbs and "Back to products". The product's own
      // folder is often a hidden one -- Harvia KIP is filed in a SHOW_WEB-off folder and
      // only shown through a cross-link into Electric Sauna Heaters -- so walk `treeIds`
      // (own folder first, then cross-links) and keep the first that opens for this
      // viewer. Usually that is the first try. An older backend without `treeIds` gets
      // the single own-folder lookup it always had.
      const candidates = product.treeIds?.length
        ? product.treeIds
        : [product.treeId];
      let catalog = null;
      for (const treeId of candidates) {
        try {
          catalog = await $axios.$get("catalog-type", { params: { treeId } });
          break;
        } catch {}
      }
      return { pageType: "product", pageData: { product, catalog } };
    };

    const loadCategory = async (category) => {
      if (category.hasChilds) {
        const categories = await $axios.$get("categories", {
          params: { parentId: category.treeId },
        });
        return {
          pageType: "category",
          pageData: { ...category, categories },
        };
      }

      const PER_PAGE = 24;
      const PAGE = parseInt(route.query.page) || 1;
      const { products, total } = await $axios.$get("products", {
        params: { treeId: SLUG, items: PER_PAGE, page: PAGE },
      });
      return {
        pageType: "category",
        pageData: {
          ...category,
          products,
          pagination: {
            perPage: PER_PAGE,
            currentPage: PAGE,
            totalPages: Math.ceil(total / PER_PAGE),
            totalProducts: total,
          },
        },
      };
    };

    const resolveSlug = async () => {
      try {
        return await $axios.$get("slug-resolve", { params: { slug: SLUG } });
      } catch {
        // The backend may not have /slug-resolve yet -- frontend and backend deploy as
        // separate containers, so either can go live first. Fall back to the old probe
        // so category pages keep rendering regardless of deploy order.
        try {
          const category = await $axios.$get("catalog-type", {
            params: { treeId: SLUG },
          });
          return { type: "category", ...category };
        } catch {}
      }
      return null;
    };

    // The hint only skips a round trip -- it never decides the page on its own, so a
    // wrong one costs a failed fetch instead of 404ing a page that exists.
    if (HINTED_PRODUCT) {
      try {
        return await loadProduct();
      } catch {}
    }

    const resolved = await resolveSlug();

    if (resolved && resolved.type === "category") {
      // Drop `type` so it does not ride along into pageData and land on the component
      // root as a stray DOM attribute -- the rest is the /catalog-type payload verbatim.
      const { type, ...category } = resolved;
      try {
        return await loadCategory(category);
      } catch {}
    }

    // Reached when the slug is a product or when the category branch failed -- the
    // product fetch stays the last-resort path either way.
    if (!HINTED_PRODUCT) {
      try {
        return await loadProduct();
      } catch {}
    }

    error({ statusCode: 404, message: "Page not found" });
  },
};
